import * as React from 'react';
import {
  Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { styled } from '@mui/material/styles';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/env';
import Button from '@mui/material/Button';
import ToolBarOrganisation from './ToolBarOrganisation';
import PaginationTable from './Pagination';
import OrganisationForm from './modal/OrganisationForm';
import { Organisation } from './types/organisation';
import OrganisationVideoImporter from './modal/OrganisationVideoImporter';

const Root = styled('div')`
    position: relative;

    .linear-bottom {
        position: absolute;
        bottom: 0;
        width: 100%;
        left: 0;
    }

    .linear-top {
        position: absolute;
        top: 0;
        width: 100%;
        left: 0;
    }
`;

const initialOrganisation: Organisation = {
  id: null,
  name: '',
  domain: '',
  org_type: [],
  rightsholders: [],
  manually_approve_video: false,
  is_active: true,
  is_premium: false,
  integration_type: null,
  auto_send_clip_basic: false,
  logo_filename: '',
  rh_video_format: ['vertical']
};

export default function AdminOrganisation() {
  const [orgs, setOrgs] = useState<Organisation[]>([]);

  const [searchInputText, setSearchInputText] = useState({
    searchText: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState('true');
  const [orgType, setOrgType] = useState('all');
  const [limit, setLimit] = useState(20);
  const [openOrgDialog, setOpenOrgDialog] = useState<Organisation>();
  const [openVideoImporter, setOpenVideoImporter] = useState<Organisation>();

  const isAdmin = localStorage.getItem('is_admin') === 'true';
  if (!isAdmin) {
    window.location.href = '/login';
  }

  const cancelTokenRef = useRef<any>();

  const updateList = async () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled due to new request.');
    }

    cancelTokenRef.current = axios.CancelToken.source();

    axios.get(
      `${getApiBaseUrl()}/organisation/list`, {
        params: {
          limit,
          page: currentPage,
          active: status === 'true',
          search: searchInputText.searchText,
          org_type: orgType !== 'all' ? orgType : '',
        },
        cancelToken: cancelTokenRef.current.token
      }
    ).then(({ data: { items, metadata } }) => {
      if (items) {
        setOrgs(items);
      }
      setTotalItems(metadata.totalItems);
    }).catch(ex => {
      if (axios.isCancel(ex)) {
        console.log('Request canceled:', ex.message);
        return;
      }
      if (ex.message) {
        alert(ex.message);
      }
      console.error(ex);
    });

    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };

  };

  const toggleStatus = async (id: number | null, newValue) => {
    await axios.patch(
      `${getApiBaseUrl()}/organisation/status`, { id, is_active: Boolean(newValue) }
    );
    updateList();
  };

  useEffect(() => {
    updateList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, searchInputText.searchText, orgType, status]);

  return (
    <React.Fragment>
      <ToolBarOrganisation
        title={'Admin Organisations'}
        setSearchInputText={setSearchInputText}
        searchInputText={searchInputText}
        setOrgType={setOrgType}
        setStatus={setStatus}
      />
      { openOrgDialog && <OrganisationForm open={Boolean(openOrgDialog)} handleClose={() => {
        setOpenOrgDialog(undefined);
        updateList();
      }} title={`Admin: ${openOrgDialog.id ? 'Edit' : 'Add'} Organisation`}
      organisation={openOrgDialog}/>}


      { openVideoImporter && <OrganisationVideoImporter open={Boolean(openVideoImporter)} handleClose={() => {
        setOpenVideoImporter(undefined);
      }} organisation={openVideoImporter}/>}

      <Root>
        <TableContainer component={Paper} sx={{}}>
          <Grid container spacing={2} justifyContent={'flex-end'} sx={{ marginBottom: 2 }}>
            <Grid item>
              <Button variant={'contained'} onClick={() => {
                setOpenOrgDialog(initialOrganisation)
              }}>Add Organisation</Button>
            </Grid>
          </Grid>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.domain}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.org_type.map(({ type }) => type).join(', ')}
                  </TableCell>
                  <TableCell align="right">
                    <Grid container justifyContent={'flex-end'} gap={1}>
                      <Grid item>
                        <Button variant={'contained'} size={'small'} onClick={() => {
                          if (window.confirm('Are you sure?')) {
                            toggleStatus(row.id, !row.is_active);
                          }
                        }}>{row.is_active ? 'Disable' : 'Enable'}</Button>
                      </Grid>
                      <Grid item>
                        <Button endIcon={<YouTubeIcon/>} variant={'contained'} size={'small'} onClick={() => {
                          setOpenVideoImporter(row);
                        }}>Video importer</Button>
                      </Grid>
                      <Grid item>
                        <Button variant={'contained'} size={'small'} onClick={() => {
                          setOpenOrgDialog(row)
                        }}>Edit</Button>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {orgs.length > 0 && (
          <PaginationTable
            defaultSize={20}
            busy={false}
            startPage={currentPage - 1}
            count={totalItems}
            onChange={page => {
              setCurrentPage(page + 1);
            }}
            updateRowPerPage={totalPerPage => {
              setLimit(totalPerPage);
              setCurrentPage(1);
            }}
          />
        )}
      </Root>
    </React.Fragment>
  );
};
